import {
    useClickOutside,
    useDropdownKeyboard,
} from "@mrflark/core/hooks";
import type {
    InvalidTagQueryResponse,
    ValidTagQueryResponse,
} from "@mrflark/spl_service_model";
import {
    useMutation,
    useQuery,
} from "@tanstack/react-query";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "preact/compat";
import {
    splClient,
} from "../../api/client.ts";

type TagQueryValidationResponse =
    | ValidTagQueryResponse
    | InvalidTagQueryResponse;
import {
    CheckCircle,
    HelpCircle,
    XCircle,
} from "lucide-preact";
import {
    TagPill,
} from "./TagPill.tsx";

type TagQueryInputProps = {
    value: string;
    onChange: (value: string) => void;
    onSubmit?: () => void;
    placeholder?: string;
};

export const TagQueryInput = ({
    value,
    onChange,
    onSubmit,
    placeholder = "e.g. (funny AND !new) OR good",
}: TagQueryInputProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [showHelp, setShowHelp] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Validate query with debounce
    // api returns either valid or invalid response as 200
    const validateMutation = useMutation({
        mutationFn: (query: string) =>
            splClient.validateTagQuery(query) as Promise<
                TagQueryValidationResponse
            >,
    });

    // Debounced validation
    useEffect(() => {
        if (!value.trim()) {
            validateMutation.reset();
            return;
        }

        const timer = setTimeout(() => {
            validateMutation.mutate(value);
        }, 300);

        return () => clearTimeout(timer);
    }, [value]);

    // Fetch suggestions for current cursor position
    const {
        data: suggestionsData,
    } = useQuery({
        queryKey: ["queryTagSuggestions", value, cursorPosition],
        queryFn: () =>
            splClient.getTagQuerySuggestions(value, String(cursorPosition), 8),
        enabled: value.length > 0 && isOpen,
        staleTime: 30000,
    });

    const suggestions = suggestionsData?.suggestions || [];

    // Reset selected index when suggestions change
    useEffect(() => {
        setSelectedIndex(0);
    }, [suggestions.length]);

    useClickOutside([inputRef, dropdownRef], () => setIsOpen(false));

    const handleSelectSuggestion = useCallback((tag: string) => {
        if (!suggestionsData) return;

        const {
            start,
            end,
        } = suggestionsData;
        const newValue = value.slice(0, start) + tag + value.slice(end);
        onChange(newValue);
        setIsOpen(false);

        // Move cursor after inserted tag
        setTimeout(() => {
            if (inputRef.current) {
                const newCursorPos = start + tag.length;
                inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                inputRef.current.focus();
            }
        }, 0);
    }, [suggestionsData, value, onChange]);

    const {
        handleKeyDown,
    } = useDropdownKeyboard({
        isOpen,
        totalOptions: suggestions.length,
        selectedIndex,
        setSelectedIndex,
        onSelect: (index) => {
            if (suggestions[index]) {
                handleSelectSuggestion(suggestions[index].tag);
            }
        },
        onClose: () => setIsOpen(false),
        onSubmitWhenClosed: () => onSubmit?.(),
    });

    const handleInputChange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        onChange(target.value);
        setCursorPosition(target.selectionStart || 0);
        setIsOpen(true);
    };

    const handleClick = () => {
        if (inputRef.current) {
            setCursorPosition(inputRef.current.selectionStart || 0);
        }
    };

    // Determine validation status
    const validationStatus = validateMutation.data?.valid === true
        ? "valid"
        : validateMutation.data?.valid === false
        ? "invalid"
        : "pending";

    const borderColor = {
        valid: "border-success focus:border-success",
        invalid: "border-error focus:border-error",
        pending: "border-border focus:border-accent",
    }[validationStatus];

    return (
        <div className="space-y-2">
            <div className="relative">
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        value={value}
                        onChange={handleInputChange}
                        onClick={handleClick}
                        onFocus={() => setIsOpen(true)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className={`input w-full pr-10 ${borderColor}`}
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        {validationStatus === "valid" && (
                            <CheckCircle size={18} className="text-success" />
                        )}
                        {validationStatus === "invalid" && (
                            <XCircle size={18} className="text-error" />
                        )}
                        <button
                            type="button"
                            onClick={() => setShowHelp(!showHelp)}
                            className="text-text-muted hover:text-text-primary transition-colors"
                        >
                            <HelpCircle size={18} />
                        </button>
                    </div>
                </div>

                {isOpen && suggestions.length > 0 && (
                    <div
                        ref={dropdownRef}
                        className="absolute z-50 mt-1 w-full bg-bg-secondary border border-border rounded-md shadow-lg max-h-48 overflow-auto"
                    >
                        {suggestions.map((suggestion, index) => (
                            <button
                                type="button"
                                key={suggestion.tag}
                                onClick={() =>
                                    handleSelectSuggestion(suggestion.tag)}
                                className={`
                    w-full px-3 py-2 text-left flex items-center justify-between
                    ${
                                    index === selectedIndex
                                        ? "bg-bg-tertiary"
                                        : "hover:bg-bg-tertiary"
                                }
                    transition-colors
                  `}
                            >
                                <span className="text-text-primary">
                                    {suggestion.tag}
                                </span>
                                <span className="text-text-muted text-sm">
                                    {suggestion.count}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Validation message */}
            {validateMutation.data && (
                <div
                    className={`text-sm ${
                        validationStatus === "valid"
                            ? "text-success"
                            : "text-error"
                    }`}
                >
                    {validateMutation.data.valid
                        ? validateMutation.data.message
                        : validateMutation.data.error}
                </div>
            )}

            {/* Tags preview */}
            {validateMutation.data?.valid && validateMutation.data.tags &&
                validateMutation.data.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {validateMutation.data.tags.map((tag) => (
                        <TagPill key={tag} tag={tag} size="sm" />
                    ))}
                </div>
            )}

            {/* Help panel */}
            {showHelp && (
                <div className="p-3 bg-bg-tertiary rounded-md text-sm text-text-secondary space-y-2">
                    <p className="font-medium text-text-primary">
                        Query Syntax Help
                    </p>
                    <ul className="space-y-1 list-disc list-inside">
                        <li>
                            <code className="text-accent">tag</code>{" "}
                            - Match items with tag
                        </li>
                        <li>
                            <code className="text-accent">!tag</code>{" "}
                            - Match items without tag
                        </li>
                        <li>
                            <code className="text-accent">(a AND b)</code>{" "}
                            - Match both tags
                        </li>
                        <li>
                            <code className="text-accent">(a OR b)</code>{" "}
                            - Match either tag
                        </li>
                        <li>
                            <code className="text-accent">
                                ((a AND b) OR c)
                            </code>{" "}
                            - Complex expressions
                        </li>
                    </ul>
                    <p className="text-text-muted">
                        Parentheses required for multi-operator expressions.
                    </p>
                </div>
            )}
        </div>
    );
};
